/**
 * Where the user came from when they registered, sent along with the register request.
 */
export type Acquisition = {
  gclid: string;
  source: "cookie" | "url";
};

const GOOGLE_ADS_CLICK_COOKIE = "_gcl_aw";

/**
 * Reads the Google Ads click id out of the `_gcl_aw` cookie gtag sets on `.virtualmike.ai`.
 * Its value is `GCL.<timestamp>.<gclid>`, and the gclid is everything after the second dot.
 *
 * @param cookie The document's cookie string.
 * @returns The gclid, or undefined when the cookie is missing or malformed.
 */
const gclidFromCookie = (cookie: string): string | undefined => {
  const entry = cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${GOOGLE_ADS_CLICK_COOKIE}=`));

  if (!entry) return undefined;

  const [prefix, _timestamp, ...gclid] = entry.slice(GOOGLE_ADS_CLICK_COOKIE.length + 1).split(".");

  if (prefix !== "GCL") return undefined;

  return gclid.join(".") || undefined;
};

/**
 * Picks the gclid to register the user with. Google's cookie wins, since it survives
 * the SSO round trip and any page the user went through; the gclid the register URL
 * carried is only the fallback for when the cookie is not there (e.g. ad cookies refused).
 *
 * @param cookie The document's cookie string.
 * @param urlGclid The gclid the register URL carried, if any.
 * @returns The acquisition to send, or undefined when there is no gclid at all.
 */
export const readAcquisition = (
  cookie: string,
  urlGclid: string | undefined
): Acquisition | undefined => {
  const cookieGclid = gclidFromCookie(cookie);

  if (cookieGclid) return { gclid: cookieGclid, source: "cookie" };

  if (urlGclid) return { gclid: urlGclid, source: "url" };

  return undefined;
};
